import axios from 'axios';
import { auth } from '../firebase';
import { BACKEND_API_BASE_URL } from '../config/env';

// API Configuration
const API_CONFIG = {
  baseURL: BACKEND_API_BASE_URL,
  timeout: 30000, // 30 seconds
  retries: 3,
  retryDelay: 1000, // 1 second
};

// Create Axios instance
const apiClient = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management
class TokenManager {
  constructor() {
    this.tokenKey = 'auth_token';
    this.refreshTokenKey = 'refresh_token';
  }

  getToken() {
    return localStorage.getItem(this.tokenKey);
  }

  setToken(token) {
    localStorage.setItem(this.tokenKey, token);
  }

  getRefreshToken() {
    return localStorage.getItem(this.refreshTokenKey);
  }

  setRefreshToken(refreshToken) {
    localStorage.setItem(this.refreshTokenKey, refreshToken);
  }

  clearTokens() {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.refreshTokenKey);
  }

  async getFirebaseToken() {
    try {
      if (auth.currentUser) {
        const token = await auth.currentUser.getIdToken();
        return token;
      }
      return null;
    } catch (error) {
      console.error('Error getting Firebase token:', error);
      return null;
    }
  }
}

const tokenManager = new TokenManager();

// Request interceptor
apiClient.interceptors.request.use(
  async (config) => {
    // Add request timestamp for logging
    config.metadata = { startTime: new Date() };

    // Add basic headers
    const headers = { ...config.headers };
    
    // Get Firebase token if available
    const firebaseToken = await tokenManager.getFirebaseToken();
    if (firebaseToken) {
      headers.Authorization = `Bearer ${firebaseToken}`;
    }

    // Add user context headers if available (for document API compatibility)
    const storedUserData = localStorage.getItem('user');
    const storedCompanyData = localStorage.getItem('company');
    
    if (storedUserData) {
      try {
        const userData = JSON.parse(storedUserData);
        if (userData.uid) headers['x-user-id'] = userData.uid;
        if (userData.email) headers['x-user-email'] = userData.email;
        if (userData.firstName) headers['x-user-first-name'] = userData.firstName;
        if (userData.lastName) headers['x-user-last-name'] = userData.lastName;
        if (userData.firstName && userData.lastName) {
          headers['x-user-name'] = `${userData.firstName} ${userData.lastName}`;
        }
      } catch (error) {
        console.warn('Failed to parse user data for headers:', error);
      }
    }

    if (storedCompanyData) {
      try {
        const companyData = JSON.parse(storedCompanyData);
        if (companyData.id) headers['x-company-id'] = companyData.id;
      } catch (error) {
        console.warn('Failed to parse company data for headers:', error);
      }
    }

    config.headers = headers;

    const method = config.method?.toUpperCase() || 'GET';
    const url = config.baseURL
      ? `${config.baseURL.replace(/\/$/, '')}/${(config.url || '').replace(/^\//, '')}`
      : config.url;

    console.log('🌐 API CALL:', method, url);

    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // Calculate request duration
    const duration = new Date() - response.config.metadata.startTime;

    // Log response in development
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ API Response:', {
        method: response.config.method?.toUpperCase(),
        url: response.config.url,
        status: response.status,
        duration: `${duration}ms`,
        data: response.data,
      });
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Calculate request duration if metadata exists
    const duration = originalRequest.metadata 
      ? new Date() - originalRequest.metadata.startTime 
      : 0;

    // Log error in development
    if (process.env.NODE_ENV === 'development') {
      console.error('❌ API Error:', {
        method: originalRequest.method?.toUpperCase(),
        url: originalRequest.url,
        status: error.response?.status,
        duration: `${duration}ms`,
        message: error.message,
        data: error.response?.data,
      });
    }

    // Handle 401 Unauthorized - simplified without token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      console.log('401 Unauthorized - no token authentication required');
    }

    // Handle network errors with retry logic
    if (
      error.code === 'NETWORK_ERROR' || 
      error.code === 'ECONNABORTED' ||
      !error.response
    ) {
      const retryCount = originalRequest._retryCount || 0;
      
      if (retryCount < API_CONFIG.retries) {
        originalRequest._retryCount = retryCount + 1;
        
        // Wait before retrying
        await new Promise(resolve => 
          setTimeout(resolve, API_CONFIG.retryDelay * Math.pow(2, retryCount))
        );
        
        return apiClient(originalRequest);
      }
    }

    return Promise.reject(error);
  }
);

// API Error Handler
export class ApiError extends Error {
  constructor(message, status, code, details) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  static fromResponse(error) {
    const response = error.response;
    const data = response?.data || {};
    
    return new ApiError(
      data.error || data.message || error.message || 'An error occurred',
      response?.status || 500,
      data.code || 'UNKNOWN_ERROR',
      data.details || null
    );
  }

  // Backend 400s carry the reason in `details`: an array of { field, message }
  // for validation failures, or a plain string for service errors.
  getDetailMessages() {
    if (Array.isArray(this.details)) {
      return this.details
        .map((detail) => (typeof detail === 'string' ? detail : detail?.message))
        .filter(Boolean)
        .map((message) => message.replace(/["']/g, '').trim());
    }

    if (typeof this.details === 'string' && this.details.trim()) {
      return [this.details.trim()];
    }

    return [];
  }

  getUserFriendlyMessage() {
    // Return user-friendly error messages based on status codes
    switch (this.status) {
      case 400: {
        const detailMessages = this.getDetailMessages();
        if (detailMessages.length) {
          return `${this.message || 'Invalid request'}: ${detailMessages.join('; ')}`;
        }
        return this.message || 'Invalid request. Please check your input.';
      }
      case 401:
        return 'You are not authorized. Please log in again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return this.message || 'This resource already exists.';
      case 422:
        return this.message || 'Invalid data provided.';
      case 429:
        return 'Too many requests. Please try again later.';
      case 500:
        return 'Server error. Please try again later.';
      case 503:
        return 'Service temporarily unavailable. Please try again later.';
      default:
        return this.message || 'An unexpected error occurred.';
    }
  }
}

// API Methods
const api = {
  // Generic HTTP methods
  async get(url, config = {}) {
    try {
      const response = await apiClient.get(url, config);
      return response.data;
    } catch (error) {
      throw ApiError.fromResponse(error);
    }
  },

  async post(url, data = {}, config = {}) {
    try {
      const response = await apiClient.post(url, data, config);
      return response.data;
    } catch (error) {
      throw ApiError.fromResponse(error);
    }
  },

  async put(url, data = {}, config = {}) {
    try {
      const response = await apiClient.put(url, data, config);
      return response.data;
    } catch (error) {
      throw ApiError.fromResponse(error);
    }
  },

  async patch(url, data = {}, config = {}) {
    try {
      const response = await apiClient.patch(url, data, config);
      return response.data;
    } catch (error) {
      throw ApiError.fromResponse(error);
    }
  },

  async delete(url, config = {}) {
    try {
      const response = await apiClient.delete(url, config);
      return response.data;
    } catch (error) {
      throw ApiError.fromResponse(error);
    }
  },

  // File upload method
  async upload(url, formData, config = {}) {
    try {
      const response = await apiClient.post(url, formData, {
        ...config,
        headers: {
          ...config.headers,
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw ApiError.fromResponse(error);
    }
  },

  // Download method
  async download(url, config = {}) {
    try {
      const response = await apiClient.get(url, {
        ...config,
        responseType: 'blob',
      });
      return response;
    } catch (error) {
      throw ApiError.fromResponse(error);
    }
  },

  // User Profile API methods
  userProfile: {
    async get() {
      return api.get('/users/profile');
    },

    async update(userData) {
      return api.put('/users/profile', userData);
    }
  },

  // User Preferences API methods (keeping for company settings)
  userPreferences: {
    async get() {
      return api.get('/user-preferences');
    },

    async update(preferencesData) {
      return api.put('/user-preferences', preferencesData);
    },

    async reset() {
      return api.post('/user-preferences/reset');
    }
  },

  // Company Settings API methods
  companySettings: {
    async get(companyId) {
      return api.get(`/companies/${companyId}/settings`);
    },

    async update(companyId, settingsData) {
      return api.put(`/companies/${companyId}/settings`, settingsData);
    },

    async reset(companyId) {
      return api.post(`/companies/${companyId}/settings/reset`);
    }
  },

  // User management API methods
  users: {
    async list(params = {}) {
      return api.get('/users', { params });
    },

    async stats() {
      return api.get('/users/stats');
    },

    async get(userId) {
      return api.get(`/users/${userId}`);
    },

    async create(userData) {
      return api.post('/users', userData);
    },

    async update(userId, updateData) {
      return api.put(`/users/${userId}`, updateData);
    },

    async delete(userId) {
      return api.delete(`/users/${userId}`);
    },

    async updatePermissions(userId, permissions) {
      return api.put(`/users/${userId}/permissions`, { permissions });
    }
  }
};

// Health check function
export const checkApiHealth = async () => {
  try {
    const response = await api.get('/health');
    return { status: 'healthy', data: response };
  } catch (error) {
    return { 
      status: 'unhealthy', 
      error: error.getUserFriendlyMessage(),
      details: error 
    };
  }
};

// Export the API instance and utilities
export { api, apiClient, tokenManager, API_CONFIG };
export default api;
